import React, { useContext, useState } from 'react';
import {
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Popconfirm, Space, Tag, Tooltip } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import { UtilityAccount } from '@totem/components/portfolio/buildings/details/utilities/types';
import UtilityAccountModal from '@totem/components/portfolio/buildings/details/utilities/UtilityAccountModal';
import UtilityAccountsContext from '@totem/components/portfolio/buildings/details/utilities/UtilityAccountsContext';
import UtilityBillsDrawer from '@totem/components/portfolio/buildings/details/utilities/UtilityBillsDrawer';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { BUILDINGS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const formatRate = (val: number) => (val > 0 ? `$${val.toFixed(4)}` : '—');

const UtilityAccountsPanel = () => {
  const { getApiToken } = useContext(UserProfileContext);
  const { data, loading, buildingId, requestRefresh } = useContext(
    UtilityAccountsContext,
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [editAccount, setEditAccount] = useState<UtilityAccount>(null);
  const [billsAccount, setBillsAccount] = useState<UtilityAccount>(null);
  const [billsOpen, setBillsOpen] = useState(false);

  const handleDelete = (account: UtilityAccount) => {
    fetch(
      `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/utility-accounts/${account.id}`,
      {
        method: 'DELETE',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    ).then((res) => {
      CheckResponseShowError(res);

      requestRefresh();
    });
  };

  const columns: ColumnProps<UtilityAccount>[] = [
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
      sorter: (compA, compB) => sortStringAscending(compA.label, compB.label),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Account Number',
      dataIndex: 'accountNumber',
      key: 'accountNumber',
      sorter: (compA, compB) =>
        sortStringAscending(compA.accountNumber, compB.accountNumber),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Utility Provider',
      dataIndex: 'utilityProvider',
      key: 'utilityProvider',
      render: (_, record) => record.utilityProvider || '—',
    },
    {
      title: 'Tariff',
      dataIndex: 'tariffSchedule',
      key: 'tariffSchedule',
      render: (_, record) => record.tariffSchedule || '—',
    },
    {
      title: 'Blended Rate',
      key: 'blendedRate',
      align: 'right' as const,
      render: (_, record) =>
        formatRate(record.rateInfo?.blendedEffectiveRate ?? 0),
    },
    {
      title: 'Target kW',
      key: 'targetKw',
      align: 'right' as const,
      render: (_, record) =>
        record.demandBaseline?.targetBaselineKw > 0
          ? record.demandBaseline.targetBaselineKw.toLocaleString()
          : '—',
    },
    {
      title: 'Rate',
      key: 'rateConfirmed',
      align: 'center' as const,
      render: (_, record) => (
        <Tag color={record.rateInfo?.rateConfirmed ? 'green' : 'default'}>
          {record.rateInfo?.rateConfirmed ? 'Confirmed' : 'Pending'}
        </Tag>
      ),
    },
    {
      title: '',
      key: 'actions',
      align: 'right' as const,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Bills">
            <Button
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => {
                setBillsAccount(record);

                setBillsOpen(true);
              }}
            >
              Bills
            </Button>
          </Tooltip>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditAccount(record);

              setModalOpen(true);
            }}
          />
          <Popconfirm
            title="Delete this account?"
            description="This cannot be undone."
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            okType="danger"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <IBCard
        isCovered
        title="Utility Accounts"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditAccount(null);

              setModalOpen(true);
            }}
          >
            Add Account
          </Button>
        }
      >
        <IBTable
          columns={columns}
          dataSource={isNotNull(data) ? data : []}
          loading={loading}
          rowKey={(record) => record.id}
          pagination={{ showSizeChanger: true }}
        />
      </IBCard>

      <UtilityAccountModal
        open={modalOpen}
        account={editAccount}
        onClose={() => {
          setModalOpen(false);

          setEditAccount(null);
        }}
      />

      {isNotNull(billsAccount) && (
        <UtilityBillsDrawer
          open={billsOpen}
          buildingId={buildingId}
          account={billsAccount}
          onClose={() => {
            setBillsOpen(false);

            setBillsAccount(null);
          }}
        />
      )}
    </>
  );
};

export default UtilityAccountsPanel;
